/**
 * -----------------------------------------------------------------------------
 * Project  : ReelConsensus app - Frontend Service: api
 * File     : api.js
 * Author   : Zachary Dry
 * Created  : 2025-09-18
 * Updated  : 2025-09-19
 * Version  : 1.0
 * License: Proprietary (Company Confidential)
 * -----------------------------------------------------------------------------
 * Description:
 *   Client-side service for API access and side effects related to api.
 *   Exports: API_BASE_URL, API_ORIGIN, addClubComment, addClubMember, api, buildApiUrl, castVote, createClub, createPoll, deletePoll, deleteUser, fetchPoll, fetchPolls, getClubComments, getClubInvite, getMovieByImdb, getMovieByTitle, getRatingOpportunities, getResults, getWatchmodeSources, getWinnersSummary, joinClub, leaveClub, listClubMembers, listClubPolls, listClubs, listUsers, login, me, plexMovies, plexSaveConfig, plexSearch, plexStatus, register, removeClubMember, searchOMDB, setAuthToken, submitRating, updateClubMember, updatePoll, updateUser
 *   
 *
 * Dependencies:
 *   - axios
 *
 * Usage:
 *   Import service: import * as api from '@/services/api';
 * -----------------------------------------------------------------------------
 */
import axios from 'axios';

// Set at build time (e.g. Cloudflare Pages env). Use your Worker URL until custom domain resolves:
// REACT_APP_API_URL=https://reelconsensus-backend.zach-b2c.workers.dev/api
// Or https://api.reelconsensus.com/api once DNS is set up for that domain.
let API_BASE = process.env.REACT_APP_API_URL;
if (!API_BASE) {
  const { hostname, origin } = window.location;
  const isLocal = hostname === 'localhost' || origin.includes(':3000');
  // Local: backend on :5000. Production: same-origin /api (only works if site proxies /api to backend).
  API_BASE = isLocal ? `${origin.replace(':3000', ':5000')}/api` : `${origin}/api`;
}
// Ensure base URL ends with /api so paths like /auth/login become /api/auth/login
if (API_BASE && !API_BASE.endsWith('/api')) {
  API_BASE = API_BASE.replace(/\/?$/, '') + '/api';
}

export const api = axios.create({ baseURL: API_BASE });

// Expose base URLs for building absolute links (e.g., <img src>)
export const API_BASE_URL = API_BASE;
export const API_ORIGIN = API_BASE.replace(/\/api$/, '');
export const buildApiUrl = (path) => `${API_ORIGIN}${path}`;

// Ensure Authorization header is present even after hard refresh
api.interceptors.request.use((config) => {
  if (!config.headers) config.headers = {};
  if (!config.headers['Authorization']) {
    const token = localStorage.getItem('token');
    if (token) config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

export function setAuthToken(token) {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
}

// Auth
export const register = (data) => api.post('/auth/register', data).then(r => r.data);
export const login = (data) => api.post('/auth/login', data).then(r => r.data);
export const me = () => api.get('/auth/me').then(r => r.data);
export const updateMe = (data) => api.put('/auth/me', data).then(r => r.data);

// Polls
export const fetchPolls = (params) => api.get('/polls', { params }).then(r => r.data);
export const fetchPoll = (id) => api.get(`/polls/${id}`).then(r => r.data);
export const createPoll = (data) => api.post('/polls', data).then(r => r.data);
export const createRandomPoll = (payload) => api.post('/polls/random', payload).then(r => r.data);
export const updatePoll = (id, data) => api.put(`/polls/${id}`, data).then(r => r.data);
export const deletePoll = (id) => api.delete(`/polls/${id}`).then(r => r.data);

// OMDB proxy
export const searchOMDB = (q) => api.get('/movies/search', { params: { q } }).then(r => r.data);
export const getMovieByImdb = (imdbID) => api.get(`/movies/${imdbID}`).then(r => r.data);
export const getMovieByTitle = (title, year) => api.get('/movies/by-title', { params: { title, year } }).then(r => r.data);

// Votes
export const castVote = (pollId, movieId) => api.post('/votes', { pollId, movieId }).then(r => r.data);
export const getResults = (pollId, opts = {}) =>
  api.get(`/votes/results/${pollId}`, opts.cacheBust ? { params: { _: Date.now() } } : {}).then(r => r.data);
export const getMyVotes = () => api.get('/votes/me').then(r => r.data);

// Ratings
export const submitRating = (pollId, movieId, rating) => api.post('/ratings', { pollId, movieId, rating }).then(r => r.data);
export const getRatingOpportunities = () => api.get('/ratings/opportunities').then(r => r.data);

// Users (admin)
export const listUsers = () => api.get('/users').then(r => r.data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data).then(r => r.data);
export const deleteUser = (id) => api.delete(`/users/${id}`).then(r => r.data);

// Winners
export const getWinnersSummary = () => api.get('/winners/summary').then(r => r.data);

// Metrics
export const getTopCreators = (limit = 10) => api.get('/metrics/top-creators', { params: { limit } }).then(r => r.data);
export const getTopWinningFilms = (limit = 5, minRatings = 3) => api.get('/metrics/top-winning-films', { params: { limit, minRatings } }).then(r => r.data);
export const getTopGenres = () => api.get('/metrics/top-genres').then(r => r.data);
export const getWeeklyWinners = (days = 7) => api.get('/metrics/weekly-winners', { params: { days } }).then(r => r.data);
export const getPollsByUserSeries = (userId, granularity = 'day') => api.get('/metrics/polls-by-user', { params: { userId, granularity } }).then(r => r.data);

// Plex
export const plexSaveConfig = (data) => api.post('/plex/config', data).then(r => r.data);
export const plexStatus = () => api.get('/plex/status').then(r => r.data);
export const plexSearch = (q) => api.get('/plex/search', { params: { q } }).then(r => r.data);
export const plexMovies = () => api.get('/plex/movies').then(r => r.data);

// Physical Media
export const searchPhysicalMedia = (q, format, year) => api.get('/physical-media/search', { params: { q, format, year } }).then(r => r.data);

// Clubs
export const createClub = (name) => api.post('/clubs', { name }).then(r => r.data);
export const listClubs = () => api.get('/clubs').then(r => r.data);
export const listClubPolls = (id) => api.get(`/clubs/${id}/polls`).then(r => r.data);
export const getClub = (id) => api.get(`/clubs/${id}`).then(r => r.data);
export const getClubInvite = (id) => api.get(`/clubs/${id}/invite`).then(r => r.data);
export const getClubWebhook = (id) => api.get(`/clubs/${id}/webhook`).then(r => r.data);
export const putClubWebhook = (id, payload) => api.put(`/clubs/${id}/webhook`, payload).then(r => r.data);
export const joinClub = (inviteCode) => api.post('/clubs/join', { inviteCode }).then(r => r.data);
export const leaveClub = (clubId) => api.post(`/clubs/${clubId}/leave`).then(r => r.data);
// Club member management
export const listClubMembers = (clubId) => api.get(`/clubs/${clubId}/members`).then(r => r.data);
// Public/member-visible list of club members (distinct from admin/manage list)
export const listClubMembersPublic = (clubId) => api.get(`/clubs/${clubId}/members/public`).then(r => r.data);
export const addClubMember = (clubId, username, role) => api.post(`/clubs/${clubId}/members`, { username, role }).then(r => r.data);
export const updateClubMember = (clubId, userId, role) => api.put(`/clubs/${clubId}/members/${userId}`, { role }).then(r => r.data);
export const removeClubMember = (clubId, userId) => api.delete(`/clubs/${clubId}/members/${userId}`).then(r => r.data);
// Duplicate declaration removed

// Club comments
export const getClubComments = (clubId, pollId) => api.get(`/comments/${clubId}/${pollId}`).then(r => r.data);
export const addClubComment = (payload) => api.post('/comments', payload).then(r => r.data);
export const getMyComments = () => api.get('/comments/me').then(r => r.data);

// Watchmode
export const getWatchmodeSources = (imdbID) => api.get(`/movies/watchmode/${imdbID}`).then(r => r.data);

// Admin (owner)
export const getDbConfig = () => api.get('/admin/db').then(r => r.data);
export const saveDbConfig = (payload) => api.put('/admin/db', payload).then(r => r.data);
export const exportDb = () => api.get('/admin/db/export', { responseType: 'blob' }).then(r => r.data);
export const importDb = (payload) => api.post('/admin/db/import', payload).then(r => r.data);

// Email verification
export const verifyEmail = (email, code) => api.post('/auth/verify-email', { email, code }).then(r => r.data);
export const resendCode = (email) => api.post('/auth/resend-code', { email }).then(r => r.data);

// Analytics
export const recordVisit = (payload) => api.post('/analytics/visit', payload).then(r => r.data);
export const captureEvent = (payload) => api.post('/analytics/event', payload).then(r => r.data);
export const getAnalyticsReport = (days = 30) => api.get('/analytics/report', { params: { days } }).then(r => r.data);


